import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { refreshOffers } from '../../../actions/offerActions';
import { getLocalItem, setLocalItem } from '../../../common';
import { enabledOfferHighlights } from '../../../config/Master';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import { ProfileMaker } from '../../../models/Profile';
import OfferHighlightModal from '../../../modules/OfferHighlight/OfferHighlightModal';
import { RootState } from '../../../store';

const SearchOfferHighlights: React.FC = () => {
  const { isMobile } = useMediaBreakpoints();

  const dispatch = useDispatch();

  const profile = useSelector((state: RootState) => state.profile);
  const offers = useSelector((state: RootState) => state.offers);

  const [shouldShowSwipeMode, setShouldShowSwipeMode] = useState<boolean | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isGenerous = useMemo(() => {
    if (typeof profile === 'undefined') {
      return undefined;
    }

    const profileModel = ProfileMaker.create(profile.profile);

    return profileModel.isGenerous();
  }, [profile]);

  const hasPendingOffers = profile.offer_counts && offers.highlights && offers.highlights.length > 0;

  const onClose = () => {
    setIsModalOpen(false);
    setLocalItem('wyp_offFirstDateOffers', true);
  };

  useEffect(() => {
    if (shouldShowSwipeMode && enabledOfferHighlights) {
      dispatch(refreshOffers('highlights', 'recent', true, 1));
    }
  }, [shouldShowSwipeMode]);

  useEffect(() => {
    if (hasPendingOffers && !getLocalItem('wyp_offFirstDateOffers')) {
      setIsModalOpen(true);
    }
  }, [hasPendingOffers, offers?.highlights, profile?.offer_counts]);

  // logic mobile GM and AM
  useEffect(() => {
    if (typeof isGenerous === 'undefined') return;

    let retryCount = 0;
    const maxRetries = 3;

    if (isMobile && isGenerous) {
      setShouldShowSwipeMode(true);
      return;
    }

    const retryInterval = setInterval(() => {
      if (isMobile) {
        if (retryCount >= maxRetries) {
          clearInterval(retryInterval);

          if (!isGenerous) {
            setShouldShowSwipeMode(false);
          }
        } else {
          retryCount++;
        }
      }
    }, 1000);

    return () => clearInterval(retryInterval);
  }, [isMobile, isGenerous]);

  // logic for desktop GM and AM
  useEffect(() => {
    if (typeof isGenerous === 'undefined') return;

    let retryCount = 0;
    const maxRetries = 3;

    const retryInterval = setInterval(() => {
      if (!isMobile) {
        if (retryCount >= maxRetries !== null) {
          clearInterval(retryInterval);
          setShouldShowSwipeMode(true);
        } else {
          retryCount++;
        }
      }
    }, 1000);

    return () => clearInterval(retryInterval);
  }, [isMobile, isGenerous]);

  return (
    <>
      {shouldShowSwipeMode && hasPendingOffers && enabledOfferHighlights && (
        <OfferHighlightModal
          containerId="OfferHighlight"
          profile={profile}
          offer={offers.highlights[0]}
          onClose={onClose}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          isFetching={offers.isFetching}
        />
      )}
    </>
  );
};

export default SearchOfferHighlights;
